/**
 * Autocomplete List Component for SearchBarInput
 * 
 * Displays the list of autocomplete suggestions with "Ma position" option
 * and optional Google search fallback.
 * 
 * @module AutocompleteList
 */

import { motion } from "motion/react";
import React, { useCallback, useMemo, useState } from "react";
import { UI_MOTION } from "../../config/uiMotion.configuration";
import { usePreventScrollBounceHook } from "../../hooks/usePreventScrollBounceHook";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { getTransportModeIconSource } from "../../tools/GeocodeTools";
import { resolveRouteTypeFranceBadgeForLine } from "../../tools/routeTypeFranceBadge.tools";
import { resolveTransitRoutesForDisplay } from "../../tools/transitRouteDisplay.tools";
import { GeocodeTransportMode, GeocodeType, SearchResultItem } from "../../types/geocode-ui.types";
import { GtfsRoute } from "../../types/Gtfs.types";
import ClickOutsideHandler from "../shared/ClickOutsideHandler";
import {
    isUiMotionEnabled,
    resolveUiEaseOut,
    resolveUiSpring,
    resolveUiStagger,
} from "../shared/motion/uiMotion.tools";

interface AutocompleteListProps {

    /** Autocomplete values array */
    "autoComplete": SearchResultItem[];

    /** My position autocomplete */
    "myPositionAutocomplete": SearchResultItem;

    /** Whether to show fallback search option */
    "showCompleteFromFallback": boolean;

    /** Current input value for fallback search */
    "input": string;

    /** Reference to results container */
    "resultsRef": React.RefObject<HTMLDivElement>;

    /** Callback when address is selected */
    "onAddressSelect": ( item: SearchResultItem ) => void;

    /** Callback when fallback search is requested */
    "onFallbackSearch": ( query: string, isExplicit?: boolean ) => void;

    /** Callback when clicking outside */
    "onOutsideClick": () => void;

    /** Whether dark palette is active for overlays */
    "isDarkUi": boolean;
}

interface ModeIcon {
    "alt": string;
    "src": string;
}

/**
 * Autocomplete List Component
 * 
 * @param {AutocompleteListProps} props - Component props
 * @returns {JSX.Element} Autocomplete list component
 */
const AutocompleteList: React.FC<AutocompleteListProps> = ( {
    autoComplete,
    myPositionAutocomplete,
    showCompleteFromFallback,
    input,
    resultsRef,
    onAddressSelect,
    onFallbackSearch,
    onOutsideClick,
    isDarkUi,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );
    const [ hoveredItemKey, setHoveredItemKey ] = useState<string | null>( null );
    const handleFallbackSearch = useCallback( () => {
        // Explicit user click: pass isExplicit=true
        void onFallbackSearch( input, true );
    }, [ onFallbackSearch, input ] );

    const autocompleteItems = useMemo( () => {
        return [
            myPositionAutocomplete,
            ...autoComplete,
        ];
    }, [ autoComplete, myPositionAutocomplete ] );

    usePreventScrollBounceHook( resultsRef );

    const getModesIcons = ( modes: GeocodeTransportMode[] ): Array<ModeIcon> => {
        const seenSources = new Set<string>();
        const icons: ModeIcon[] = [];

        modes.forEach( ( mode: GeocodeTransportMode ) => {
            const iconSource = getTransportModeIconSource( mode );
            if ( seenSources.has( iconSource ) ) {
                return;
            }

            seenSources.add( iconSource );
            icons.push( {
                "alt": mode,
                "src": iconSource,
            } );
        } );

        return icons;
    };

    return (
        <motion.div
            animate={animationsEnabled
                ? {
                    "opacity": 1,
                    "scaleY":  1,
                }
                : undefined}
            exit={animationsEnabled
                ? {
                    "opacity":    0,
                    "scaleY":     0.96,
                    "transition": resolveUiEaseOut( reducedMotion, UI_MOTION.duration.fastMs ),
                }
                : undefined}
            initial={animationsEnabled
                ? {
                    "opacity": 0,
                    "scaleY":  0.96,
                }
                : undefined}
            style={animationsEnabled
                ? {
                    "transformOrigin": "top center",
                    "willChange":      "transform, opacity",
                }
                : undefined}
            transition={resolveUiSpring( reducedMotion, "gentle" )}
        >
            <ClickOutsideHandler
                className="mp-0 px-0 backdrop-blur-[5px] shadow-2xl rounded-b-2xl cursor-pointer max-h-96 overflow-y-auto overscroll-contain touch-pan-y no-bounce [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                onOutsideClick={onOutsideClick}
                wrapperRef={resultsRef}
            >
                { autocompleteItems.map( ( item: SearchResultItem, itemIndex: number ) => {
                // Generate unique key from result, location, and area to ensure re-render when subtitle changes
                const areaKey = item.area ? item.area.tree.map( ( a ) => a.name ).join( "-" ) : "";
                const itemKey = `${ item.result }-${ item.location.coordinates.lat }-${ item.location.coordinates.lon }-${ item.source }-${ areaKey }`;
                const isThemeTintedGeocodingIcon =
                    item.icon.src === "/assets/icon/geocoding/place.svg" ||
                    item.icon.src === "/assets/icon/geocoding/address.svg" ||
                    item.icon.src === "/assets/icon/geocoding/osm/christian_16.svg" ||
                    item.icon.alt === "Ma position";

                const itemContent = (
                    <div
                        className="cursor-pointer border-ionic-border-light-gray flex w-full select-none py-3 px-3 text-sm outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:opacity-50 items-center transition-colors duration-150 overflow-hidden"
                        onClick={() => {
                            onAddressSelect( item );
                        }}
                        onMouseEnter={() => {
                            setHoveredItemKey( itemKey );
                        }}
                        onMouseLeave={() => {
                            setHoveredItemKey( null );
                        }}
                        style={{
                            "background": hoveredItemKey === itemKey
                                ? palette.autocompleteItemBackgroundGradientHover
                                : palette.autocompleteItemBackgroundGradient,
                            "color": palette.overlayTextPrimary,
                        }}
                    >
                        <div className="flex items-center grow min-w-0">
                            {
                                item.type === GeocodeType.STOP && (
                                    <div className="rounded-full bg-emerald-500/20 w-[32px] h-[32px] flex items-center justify-center">
                                        <img
                                            alt={item.icon.src}
                                            className="w-[16px] h-[16px] object-contain"
                                            src={item.icon.src}
                                        />
                                    </div>
                                )
                            }
                            {
                                item.type !== GeocodeType.STOP && (
                                    <div className="flex items-center justify-center p-1 w-[32px] h-[32px]">
                                        <img
                                            alt={item.icon.alt}
                                            className="w-[24px] h-[24px] object-contain"
                                            src={item.icon.src}
                                            style={isThemeTintedGeocodingIcon && isDarkUi
                                                ? { "filter": "invert(80%) sepia(8%) saturate(296%) hue-rotate(178deg) brightness(95%) contrast(92%)" }
                                                : undefined}
                                        />
                                    </div>
                                )
                            }
                            <div className="flex flex-col ml-4 min-w-0">
                                <span className="font-semibold truncate">
                                    { item.title }
                                </span>
                                <span className="text-muted-foreground truncate">
                                    { item.subtitle }
                                </span>
                            </div>
                        </div>
                        {
                            item.transit && ( () => {
                                const {
                                    displayedRoutes,
                                    showEllipsis,
                                    useThreeColumnGrid,
                                } = resolveTransitRoutesForDisplay( item.transit.routes );
                                const gridColRow = useThreeColumnGrid ? "grid-cols-3" : "";

                                return ( <div className={`grid grid-flow-row ${ gridColRow } h-[100%] gap-1`}>
                                    {
                                        displayedRoutes.map( ( route: GtfsRoute ) => {
                                            const franceBadge = resolveRouteTypeFranceBadgeForLine( route.routeTypeFrance, route.name );
                                            const paddingX = franceBadge !== null
                                                ? "px-[10px]"
                                                : route.name.length === 1 ? "px-[14px]" : "px-[10px]";

                                            return (
                                                    <div
                                                        className={`${ paddingX } py-[1px] h-[100%] rounded-full font-bold text-xs flex-shrink-0 text-center flex items-center justify-center`}
                                                        key={`${ itemKey }-route-${ route.routeTypeFrance ?? route.name }`}
                                                        style={franceBadge !== null
                                                            ? { "backgroundColor": "#ffffff" }
                                                            : {
                                                                "backgroundColor": route.color ?? "#13d39c",
                                                                "color":           route.textColor ?? "#000000",
                                                            }}
                                                        title={franceBadge !== null ? `${ franceBadge.label } — ${ route.name }` : route.name}
                                                    >
                                                        {franceBadge !== null
                                                            ? (
                                                                <img
                                                                    alt={franceBadge.label}
                                                                    className="h-3.5 w-auto object-contain"
                                                                    src={franceBadge.icon}
                                                                />
                                                            )
                                                            : route.name}
                                                    </div>
                                            );
                                        } )
                                    }
                                    {
                                        showEllipsis && (
                                            <div
                                                className="px-[10px] py-[1px] h-[100%] rounded-full font-bold text-xs flex-shrink-0 text-center"
                                                style={{
                                                            "backgroundColor": "#cecece",
                                                            "color":           "#000000",
                                                        }}
                                            >
                                                        ...
                                            </div>
                                        )
                                    }
                                         </div> );
                            } )()
                        }
                        {
                            !item.transit && item.modes && (
                                <div className="flex flex-row items-center">
                                    {
                                        getModesIcons( item.modes ).map( ( mode: ModeIcon, index: number ) => {
                                            return (
                                                <div
                                                    className="rounded-full flex items-center justify-center"
                                                    key={`${ itemKey }-mode-${ index }`}
                                                >
                                                    <img
                                                        alt={mode.alt}
                                                        className="max-w-sm mr-4 w-[20px] h-[20px] object-contain"
                                                        src={mode.src}
                                                    />
                                                </div>
                                            );
                                        } )
                                    }
                                </div>
                            )
                        }
                    </div>
                );

                if ( !animationsEnabled ) {
                    return (
                        <React.Fragment key={itemKey}>
                            {itemContent}
                        </React.Fragment>
                    );
                }

                return (
                    <motion.div
                        animate={{
                            "opacity": 1,
                            "y":       0,
                        }}
                        initial={{
                            "opacity": 0,
                            "y":       8,
                        }}
                        key={itemKey}
                        transition={{
                            ...resolveUiSpring( reducedMotion, "gentle" ),
                            "delay": resolveUiStagger( itemIndex, reducedMotion ),
                        }}
                    >
                        {itemContent}
                    </motion.div>
                );
            } ) }
                {showCompleteFromFallback && (
                    animationsEnabled
                        ? (
                            <motion.h3
                                animate={{
                                    "opacity": 1,
                                    "y":       0,
                                }}
                                className={`pt-5 pb-5 cursor-pointer w-full font-medium text-sm text-center ${
                                    isDarkUi
                                        ? "bg-slate-900/50 hover:bg-slate-800/70"
                                        : "bg-white/30 hover:bg-slate-100/60 text-ionic-brand-blue"
                                }`}
                                initial={{
                                    "opacity": 0,
                                    "y":       8,
                                }}
                                onClick={handleFallbackSearch}
                                style={isDarkUi ? { "color": palette.overlayTextPrimary } : undefined}
                                transition={{
                                    ...resolveUiSpring( reducedMotion, "gentle" ),
                                    "delay": ( autocompleteItems.length * UI_MOTION.staggerItemMs +
                                      UI_MOTION.staggerTrailingMs ) / 1000,
                                }}
                            >
                                Recherche approfondie...
                            </motion.h3>
                        )
                        : (
                            <h3
                                className={`pt-5 pb-5 cursor-pointer w-full font-medium text-sm text-center ${
                                    isDarkUi
                                        ? "bg-slate-900/50 hover:bg-slate-800/70"
                                        : "bg-white/30 hover:bg-slate-100/60 text-ionic-brand-blue"
                                }`}
                                onClick={handleFallbackSearch}
                                style={isDarkUi ? { "color": palette.overlayTextPrimary } : undefined}
                            >
                                Recherche approfondie...
                            </h3>
                        )
                )}
            </ClickOutsideHandler>
        </motion.div>
    );
};

export default React.memo( AutocompleteList );
